<template>
  <div class="row mb-4">
    <div class="col-12">
      <h5 class="mb-3">Horarios por Día</h5>
      <div v-for="(day, index) in days" :key="index" class="mb-4">
        <div class="row">
          <div class="col-lg-2 col-md-2 col-12">
            <label
              class="form-label"
              style="font-weight: 600; margin-bottom: 10px"
              >Día {{ index + 1 }}</label
            >
          </div>
          <div class="col-lg-5 col-md-5 col-12">
            <div class="mb-3">
              <label class="form-label">Hora de Inicio</label>
              <select
                class="form-control"
                :class="{
                  'is-invalid': getValidationError(index, 'start'),
                }"
                v-model="schedules[index].start"
                @change="validateTimeRange(index, 'start')"
              >
                <option value="">Seleccionar hora de inicio</option>
                <option v-for="time in timeOptions" :key="time" :value="time">
                  {{ time }}
                </option>
              </select>
              <div
                v-if="getValidationError(index, 'start')"
                class="invalid-feedback"
              >
                {{ getValidationError(index, "start") }}
              </div>
            </div>
          </div>
          <div class="col-lg-5 col-md-5 col-12">
            <div class="mb-3">
              <label class="form-label">Hora de Fin</label>
              <select
                class="form-control"
                :class="{
                  'is-invalid': getValidationError(index, 'end'),
                }"
                v-model="schedules[index].end"
                @change="validateTimeRange(index, 'end')"
              >
                <option value="">Seleccionar hora de fin</option>
                <option v-for="time in timeOptions" :key="time" :value="time">
                  {{ time }}
                </option>
              </select>
              <div
                v-if="getValidationError(index, 'end')"
                class="invalid-feedback"
              >
                {{ getValidationError(index, "end") }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <input type="hidden" name="schedules_json" :value="serializedSchedules" />
    </div>
  </div>
</template>

<script setup>
import { watch, ref, computed, onMounted } from "vue";

const props = defineProps({
  duration: {
    type: Number,
    default: 0,
  },
  initialSchedules: {
    type: String,
    default: "",
  },
});

const schedules = ref([]);
const validationErrors = ref({});

const timeOptions = computed(() => {
  const options = [];
  for (let hour = 8; hour <= 22; hour++) {
    if (hour === 8) {
      options.push("08:30");
    } else if (hour === 22) {
      options.push("22:00");
      options.push("22:30");
    } else {
      const hourStr = hour.toString().padStart(2, "0");
      options.push(`${hourStr}:00`);
      options.push(`${hourStr}:30`);
    }
  }
  return options;
});

const days = computed(() => {
  return Array.from({ length: props.duration }, (_, i) => i + 1);
});

function validateTimeRange(dayIndex, timeType) {
  const timeValue = schedules.value[dayIndex][timeType];

  if (!timeValue) {
    clearValidationError(dayIndex, timeType);
    return;
  }

  const errorKey = `${dayIndex}_${timeType}`;

  if (timeValue < "08:30" || timeValue > "22:30") {
    validationErrors.value[errorKey] = "Hora debe estar entre 08:30 y 22:30";
  } else {
    if (timeType === "start") {
      const endTime = schedules.value[dayIndex].end;
      if (endTime && timeValue >= endTime) {
        validationErrors.value[errorKey] =
          "Hora de inicio debe ser menor que la de fin";
      } else {
        delete validationErrors.value[errorKey];
      }
    } else if (timeType === "end") {
      const startTime = schedules.value[dayIndex].start;
      if (startTime && timeValue <= startTime) {
        validationErrors.value[errorKey] =
          "Hora de fin debe ser mayor que la de inicio";
      } else {
        delete validationErrors.value[errorKey];
      }
    } else {
      delete validationErrors.value[errorKey];
    }
  }
}

function getValidationError(dayIndex, timeType) {
  const errorKey = `${dayIndex}_${timeType}`;
  return validationErrors.value[errorKey] || null;
}

function clearValidationError(dayIndex, timeType) {
  const errorKey = `${dayIndex}_${timeType}`;
  delete validationErrors.value[errorKey];
}

function createEmptySchedule() {
  return {
    start: "",
    end: "",
  };
}

function initializeSchedules(duration) {
  if (duration <= 0) {
    schedules.value = [];
    return;
  }

  schedules.value = Array.from({ length: duration }, () =>
    JSON.parse(JSON.stringify(createEmptySchedule()))
  );
}

function loadExistingSchedules(schedulesJsonString) {
  if (!schedulesJsonString) return;

  try {
    const data = JSON.parse(schedulesJsonString);

    Object.keys(data).forEach((dayKey) => {
      const dayMatch = dayKey.match(/dia(\d+)/);
      if (dayMatch) {
        const dayIndex = parseInt(dayMatch[1]) - 1;

        if (dayIndex >= 0 && dayIndex < schedules.value.length) {
          const dayData = data[dayKey];

          if (dayData.horarios && Array.isArray(dayData.horarios)) {
            const firstSchedule = dayData.horarios.find(
              (horario) => horario.inicio && horario.fin
            );

            if (firstSchedule) {
              schedules.value[dayIndex] = {
                start: firstSchedule.inicio || "",
                end: firstSchedule.fin || "",
              };
            }
          }
        }
      }
    });
  } catch (e) {
    console.warn("Error parsing existing schedules:", e);
  }
}

watch(
  () => props.duration,
  (newDuration) => {
    initializeSchedules(newDuration);
    validationErrors.value = {};

    if (props.initialSchedules) {
      loadExistingSchedules(props.initialSchedules);
    }
  },
  { immediate: true }
);

watch(
  () => props.initialSchedules,
  (newInitialSchedules) => {
    if (newInitialSchedules && schedules.value.length > 0) {
      loadExistingSchedules(newInitialSchedules);
    }
  }
);

watch(
  schedules,
  (newSchedules) => {
    newSchedules.forEach((day, dayIndex) => {
      if (day.start) {
        validateTimeRange(dayIndex, "start");
      }
      if (day.end) {
        validateTimeRange(dayIndex, "end");
      }
    });
  },
  { deep: true }
);

const serializedSchedules = computed(() => {
  const result = {};
  schedules.value.forEach((day, i) => {
    result[`dia${i + 1}`] = {
      horarios: [
        {
          inicio: day.start,
          fin: day.end,
        },
      ],
    };
  });
  return JSON.stringify(result);
});

function hasValidationErrors() {
  return Object.keys(validationErrors.value).length > 0;
}

function getFirstHour() {
  if (schedules.value.length === 0) return null;

  const firstDay = schedules.value[0];
  return firstDay.start || null;
}

onMounted(() => {
  if (props.duration > 0) {
    initializeSchedules(props.duration);

    if (props.initialSchedules) {
      loadExistingSchedules(props.initialSchedules);
    }
  }
});

defineExpose({
  getScheduleJson: () => serializedSchedules.value,
  hasValidationErrors: () => hasValidationErrors(),
  getFirstHour: () => getFirstHour(),
});
</script>
